import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Box, Typography, TextField, Button, Paper, InputAdornment,
  IconButton, Alert, CircularProgress, Divider, Chip,
} from '@mui/material';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import { useNextGenAuth } from '../context/NextGenAuthContext';
import API_BASE_URL from '../../Config/config';

const TSGLogo = ({ size = 44, onDark = false }) => {
  const [imgError, setImgError] = useState(false);
  if (imgError) {
    return (
      <Box sx={{
        width: size, height: size, borderRadius: '12px', flexShrink: 0,
        background: onDark ? 'rgba(255,255,255,0.18)' : 'linear-gradient(135deg,#6C3BFF,#8B5CF6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: size * 0.46 }}>T</Typography>
      </Box>
    );
  }
  if (onDark) {
    return (
      <Box sx={{
        bgcolor: '#FFFFFF', borderRadius: '10px', px: 1, py: '5px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
      }}>
        <Box component="img"
          src="https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/TSG_Logo.png"
          alt="The Skill Genie" loading="lazy" onError={() => setImgError(true)}
          sx={{ height: size * 0.72, width: 'auto', objectFit: 'contain' }}
        />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src="https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/TSG_Logo.png"
      alt="The Skill Genie"
      loading="lazy"
      onError={() => setImgError(true)}
      sx={{ height: size, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
    />
  );
};

const heroFeatures = [
  { Icon: AutoAwesomeRoundedIcon, title: 'AI-Powered Reviews', desc: 'Instant feedback on your code and architecture' },
  { Icon: PeopleAltRoundedIcon, title: 'Expert Mentorship', desc: 'Learn from seasoned industry professionals' },
  { Icon: RocketLaunchRoundedIcon, title: 'Build Real Products', desc: 'Ship portfolio-worthy projects with guidance' },
  { Icon: EmojiEventsRoundedIcon, title: 'Career Acceleration', desc: 'Land top roles with verified credentials' },
];

const NextGenLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useNextGenAuth();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/nextgen-builder/login`, formData);
      if (res.data.success) {
        const { builder: nextgen_builder, sessionToken } = res.data.data;
        // Builder session token: required by the feature-billing money
        // routes (cart / checkout / orders). The shared axios interceptor
        // already sends localStorage.session_token as `Authorization: Bearer`.
        if (sessionToken) localStorage.setItem('session_token', sessionToken);
        else localStorage.removeItem('session_token');
        login(nextgen_builder);
        const from = location.state && location.state.from && String(location.state.from).startsWith('/nextgen-builder') ? location.state.from : '/nextgen-builder/dashboard';
        navigate(from);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', fontFamily: '"Plus Jakarta Sans","Inter",system-ui,sans-serif' }}>

      {/* ── Left Hero Panel ─────────────────────────────────────────── */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        width: { md: '46%' },
        flexShrink: 0,
        background: 'linear-gradient(150deg, #3A18C8 0%, #6C3BFF 48%, #9061F9 100%)',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: { md: 5, lg: 7 },
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative background elements */}
        <Box sx={{ position:'absolute',top:-120,right:-120,width:360,height:360,borderRadius:'50%',
                    background:'rgba(255,255,255,0.06)',pointerEvents:'none' }} />
        <Box sx={{ position:'absolute',bottom:-80,left:-80,width:280,height:280,borderRadius:'50%',
                    background:'rgba(255,255,255,0.04)',pointerEvents:'none' }} />
        <Box sx={{ position:'absolute',top:'38%',right:'8%',width:160,height:160,borderRadius:'50%',
                    background:'rgba(255,255,255,0.03)',pointerEvents:'none' }} />
        <Box sx={{ position:'absolute',inset:0,
                    backgroundImage:'radial-gradient(rgba(255,255,255,0.06) 1px,transparent 1px)',
                    backgroundSize:'28px 28px',pointerEvents:'none' }} />

        {/* Top: logo + badge + headline + features */}
        <Box sx={{ position:'relative', zIndex:1 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:5 }}>
            <TSGLogo size={40} onDark />
            <Typography sx={{ color:'rgba(255,255,255,0.92)', fontWeight:700, fontSize:'0.95rem' }}>
              The Skill Genie
            </Typography>
          </Box>

          <Chip label="TSG NEXUS PLATFORM" size="small" sx={{
            mb:2.5, bgcolor:'rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.92)',
            border:'1px solid rgba(255,255,255,0.22)', fontWeight:700,
            fontSize:'0.6rem', letterSpacing:'0.1em', height:24,
          }} />

          <Typography variant="h3" sx={{
            color:'#FFF', fontWeight:800, letterSpacing:'-0.03em',
            lineHeight:1.12, mb:2.5,
            fontSize: { md:'1.9rem', lg:'2.2rem' },
          }}>
            Build the Next<br />Generation of<br />Products
          </Typography>

          <Typography sx={{
            color:'rgba(255,255,255,0.7)', fontSize:'0.95rem',
            lineHeight:1.75, mb:4.5, maxWidth:360,
          }}>
            Join ambitious builders shipping real products with AI-powered reviews and expert mentorship.
          </Typography>

          <Box sx={{ display:'flex', flexDirection:'column', gap:2.5 }}>
            {heroFeatures.map(({ Icon, title, desc }) => (
              <Box key={title} sx={{ display:'flex', alignItems:'flex-start', gap:1.75 }}>
                <Box sx={{
                  width:38, height:38, borderRadius:'11px', flexShrink:0,
                  bgcolor:'rgba(255,255,255,0.12)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  border:'1px solid rgba(255,255,255,0.1)',
                }}>
                  <Icon sx={{ color:'#fff', fontSize:18 }} />
                </Box>
                <Box>
                  <Typography sx={{ color:'#fff', fontWeight:700, fontSize:'0.875rem', lineHeight:1.3 }}>
                    {title}
                  </Typography>
                  <Typography sx={{ color:'rgba(255,255,255,0.6)', fontSize:'0.78rem', lineHeight:1.55, mt:0.3 }}>
                    {desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

      </Box>

      {/* ── Right Form Panel ────────────────────────────────────────── */}
      <Box sx={{
        flex:1, bgcolor:'#F8FAFC',
        display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center',
        px:{ xs:2, sm:4, md:5 }, py:5,
        overflowY:'auto', minHeight:'100vh',
      }}>
        {/* Mobile-only logo */}
        <Box sx={{ display:{ xs:'flex', md:'none' }, flexDirection:'column',
                    alignItems:'center', mb:4, gap:1.5 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
            <TSGLogo size={36} />
            <Typography sx={{ fontWeight:700, color:'#0F172A', fontSize:'0.95rem' }}>
              The Skill Genie
            </Typography>
          </Box>
          <Chip label="TSG NEXUS" size="small" sx={{
            bgcolor:'rgba(108,59,255,0.07)', color:'#6C3BFF',
            border:'1px solid rgba(108,59,255,0.18)', fontWeight:700,
            fontSize:'0.6rem', letterSpacing:'0.08em', height:22,
          }} />
        </Box>

        <motion.div
          initial={{ opacity:0, y:18 }}
          animate={{ opacity:1, y:0 }}
          transition={{ duration:0.35, ease:[0.25,0.1,0.25,1] }}
          style={{ width:'100%', maxWidth:440 }}
        >
          <Paper elevation={0} sx={{
            p:{ xs:3.5, sm:5 },
            borderRadius:'20px',
            border:'1px solid #E5E7EB',
            bgcolor:'#FFFFFF',
            boxShadow:'0 1px 3px rgba(0,0,0,0.06),0 4px 8px rgba(0,0,0,0.04),0 20px 48px -12px rgba(108,59,255,0.1)',
          }}>
            <Box sx={{ mb:3.5 }}>
              <Typography variant="h5" sx={{ color:'#0F172A', fontWeight:800, mb:0.75 }}>
                Welcome back
              </Typography>
              <Typography variant="body2" sx={{ color:'#64748B' }}>
                Sign in to your TSG Nexus account
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb:2.5 }}>{error}</Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                fullWidth label="Username or Email" name="username" required
                value={formData.username} onChange={handleChange}
                autoComplete="username"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonRoundedIcon sx={{ color:'#94A3B8', fontSize:19 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb:2.5 }}
              />

              <TextField
                fullWidth label="Password" name="password" required
                type={showPass ? 'text' : 'password'}
                value={formData.password} onChange={handleChange}
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRoundedIcon sx={{ color:'#94A3B8', fontSize:19 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPass((p) => !p)} edge="end" size="small"
                        sx={{ color:'#94A3B8', '&:hover':{ color:'#6C3BFF', bgcolor:'rgba(108,59,255,0.07)' } }}>
                        {showPass
                          ? <VisibilityOffRoundedIcon fontSize="small" />
                          : <VisibilityRoundedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb:1 }}
              />

              <Box sx={{ textAlign:'right', mb:3.5 }}>
                <Link to="/nextgen-builder/forgot-password" style={{ color:'#6C3BFF', fontWeight:600,
                    textDecoration:'none', fontSize:'0.82rem' }}>
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit" fullWidth size="large" disabled={loading}
                sx={{
                  py:1.65, borderRadius:'12px', fontWeight:700, fontSize:'0.95rem',
                  letterSpacing:'-0.01em', color:'#fff',
                  background:'linear-gradient(135deg, #6C3BFF 0%, #8B5CF6 100%)',
                  boxShadow:'0 4px 14px rgba(108,59,255,0.32)',
                  '&:hover': {
                    background:'linear-gradient(135deg, #5020E0 0%, #7C3AED 100%)',
                    boxShadow:'0 8px 28px rgba(108,59,255,0.44)',
                    transform:'translateY(-1px)',
                  },
                  '&:disabled':{ opacity:0.6, boxShadow:'none', transform:'none' },
                }}
              >
                {loading ? <CircularProgress size={20} sx={{ color:'#fff' }} /> : 'Sign In'}
              </Button>
            </Box>

            <Divider sx={{ my:3, '&::before,&::after':{ borderColor:'#E5E7EB' } }}>
              <Typography variant="caption" sx={{ color:'#94A3B8', fontWeight:500, px:1 }}>
                or
              </Typography>
            </Divider>

            <Box sx={{ textAlign:'center' }}>
              <Typography variant="body2" sx={{ color:'#64748B' }}>
                Don't have an account?{' '}
                <Link to="/nextgen-builder/register"
                  style={{ color:'#6C3BFF', fontWeight:700, textDecoration:'none' }}>
                  Create Account
                </Link>
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Box>
    </Box>
  );
};

export default NextGenLogin;
